import React, { useState, useEffect } from 'react';
import { FaBell, FaUserCircle, FaSignOutAlt, FaBars, FaSpinner } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useMobileMenu } from '../../overall_manager/contexts/MobileMenuContext';
import { getToken, clearToken, checkTokenAndRedirect } from '../../../utils/tokenManager';
import './Topbar.css';

const Topbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showDropdown, setShowDropdown] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [animatedText, setAnimatedText] = useState("Welcome!");
    const { toggleMobileMenu } = useMobileMenu();
    const baseUrl = "https://api.burrayhr.com";

    useEffect(() => {
        // Check token validity on component mount
        if (!checkTokenAndRedirect(navigate)) {
            return;
        }
        fetchUserProfile();

        // Set up interval to check token validity every minute
        const tokenCheckInterval = setInterval(() => {
            if (!checkTokenAndRedirect(navigate)) {
                clearInterval(tokenCheckInterval);
            }
        }, 60000);

        // Cleanup interval on component unmount
        return () => clearInterval(tokenCheckInterval);
    }, [navigate]);

    useEffect(() => {
        if (!userProfile) return;

        const getGreeting = () => {
            const hour = new Date().getHours();
            const name = userProfile.first_name || 'Admin';
            if (hour < 12) return `Good Morning, ${name}`;
            if (hour < 18) return `Good Afternoon, ${name}`;
            return `Good Evening, ${name}`;
        };

        const companyName = userProfile?.company_data?.name || 'the company';

        const messages = [
            getGreeting(),
            `Welcome to ${companyName}!`,
            `${companyName} runs on your magic.`,
            `Shaping the future of ${companyName}.`,
            `Making ${companyName} a great place to work.`,
            `Excellence at ${companyName} starts with you.`
        ];

        let currentIndex = 0;
        setAnimatedText(messages[currentIndex]);

        const interval = setInterval(() => {
            currentIndex = (currentIndex + 1) % messages.length;
            setAnimatedText(messages[currentIndex]);
        }, 5000);

        return () => clearInterval(interval);
    }, [userProfile]);

    // Check first login and redirect if necessary
    useEffect(() => {
        if (userProfile?.first_login) {
            const currentPath = location.pathname;
            if (currentPath !== '/admin-manager/change-password') {
                Swal.fire({
                    icon: 'warning',
                    title: 'Change Password Required',
                    text: 'You must change your password before accessing other pages.',
                    confirmButtonColor: '#1e3a8a',
                    allowOutsideClick: false,
                    allowEscapeKey: false
                }).then(() => {
                    navigate('/admin-manager/change-password');
                });
            }
        }
    }, [userProfile, location.pathname, navigate]);

    const fetchUserProfile = async () => {
        try {
            const token = getToken();
            if (!token) {
                navigate('/');
                return;
            }

            const response = await axios.get(`${baseUrl}/api/profile/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            console.log("user profile", response.data);
            setUserProfile(response.data);

            // Check first login immediately after fetching profile
            if (response.data?.first_login) {
                const currentPath = location.pathname;
                if (currentPath !== '/admin-manager/change-password') {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Change Password Required',
                        text: 'You must change your password before accessing other pages.',
                        confirmButtonColor: '#1e3a8a',
                        allowOutsideClick: false,
                        allowEscapeKey: false
                    }).then(() => {
                        navigate('/admin-manager/change-password');
                    });
                }
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching user profile:', error);
            if (error.response?.status === 401) {
                clearToken();
                navigate('/login');
            }
            setLoading(false);
        }
    };

    const handleLogout = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You will be logged out of the system",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#95a5a6',
            confirmButtonText: 'Yes, logout'
        }).then((result) => {
            if (result.isConfirmed) {
                clearToken();
                navigate('/');
            }
        });
    };

    // Prevent dropdown menu from showing if it's first login
    const handleDropdownClick = () => {
        if (userProfile?.user_data?.first_login) {
            Swal.fire({
                icon: 'warning',
                title: 'Change Password Required',
                text: 'You must change your password before accessing other features.',
                confirmButtonColor: '#1e3a8a'
            });
            return;
        }
        setShowDropdown(!showDropdown);
    };

    return (
        <div className="topbar">
            <div className="topbar-left">
                {/* Only show menu button if not first login */}
                {!userProfile?.user_data?.first_login && (
                    <button className="menu-btn" onClick={toggleMobileMenu} aria-label="Toggle Menu">
                        <FaBars style={{fontSize: '1rem'}} className="menu-icon" />
                    </button>
                )}
                <p key={animatedText} className="animated-greeting" style={{ marginLeft: userProfile?.first_login ? '20px' : '1rem' }}>
                    {animatedText}
                </p>
            </div>
            
            <div className="topbar-right">
                <div className="user-profile" onClick={handleDropdownClick}>
                    {loading ? (
                        <FaSpinner className="spinner" />
                    ) : (
                        <>
                            {userProfile?.employee_data?.photo ? (
                                <img 
                                    style={{
                                        width: '50px', 
                                        height: '50px', 
                                        backgroundColor: 'white', 
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }} 
                                    src={`${userProfile.employee_data.photo}`} 
                                    alt="User Profile" 
                                    className="sidebar-logo" 
                                />
                            ) : (
                                <FaUserCircle 
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        color: 'white'
                                    }}
                                    className="avatar" 
                                />
                            )}
                            <span className="username">
                                {userProfile ? `${userProfile.first_name} ${userProfile.last_name}` : 'User'}
                            </span>
                            
                            {showDropdown && !userProfile?.user_data?.first_login && (
                                <div className="dropdown-menu">
                                    <div className="dropdown-header">
                                        <FaUserCircle className="dropdown-avatar" />
                                        <div className="user-info">
                                            <span className="user-name">
                                                {userProfile ? `${userProfile.first_name} ${userProfile.last_name}` : 'User'}
                                            </span>
                                            <span className="user-email">{userProfile?.email}</span>
                                            <span className="user-role">{userProfile?.role || 'Admin Manager'}</span>
                                        </div>
                                    </div>
                                    <div className="dropdown-divider"></div>
                                    <button className="dropdown-item" onClick={handleLogout}>
                                        <FaSignOutAlt />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <style jsx>{`
                .spinner {
                    animation: spin 1s linear infinite;
                    font-size: 1.5rem;
                    color: #2563eb;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .user-email {
                    font-size: 0.85rem;
                    color: #64748b;
                    display: block;
                    margin: 2px 0;
                }
            `}</style>

            <style jsx global>{`
                .animated-greeting {
                    animation: fadeIn 0.8s ease-in-out;
                    font-size: 1.2rem;
                    font-weight: 500;
                    color: #fff;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default Topbar;
